import { PhoneNumberUtil } from 'google-libphonenumber';
import type { ComponentProps } from 'react';
import {
  type FieldValues,
  type Path,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { InputField } from '#pie/components/input-field/InputField';
import { useExtendedRules } from '../formHelpers';

const phoneNumberUtil = PhoneNumberUtil.getInstance();

type FormPhoneRules<T extends FieldValues> = Omit<
  NonNullable<UseControllerProps<T>['rules']>,
  'validate'
>;

type FormPhoneProps<T extends FieldValues> = Omit<
  ComponentProps<typeof PhoneInput>,
  'value' | 'onChange' | 'id' | 'name'
> &
  Pick<UseControllerProps<T>, 'disabled' | 'name' | 'defaultValue'> & {
    rules?: FormPhoneRules<T>;
  } & Pick<ComponentProps<typeof InputField>, 'label' | 'className'>;

export function FormPhone<T extends FieldValues>({
  name,
  disabled,
  rules: originalRules,
  defaultValue,
  className,
  ...props
}: FormPhoneProps<T>) {
  const { t } = useTranslation();
  const rules = useExtendedRules(originalRules);

  const validatePhone = (value?: string) => {
    if (!value) return true;

    try {
      const phoneNumber = phoneNumberUtil.parseAndKeepRawInput(value, 'NL');

      return (
        phoneNumberUtil.isValidNumber(phoneNumber) ||
        t('common.errors.invalid_phone')
      );
    } catch {
      return t('common.errors.invalid_phone');
    }
  };

  const mergedRules = {
    ...rules,
    validate: validatePhone,
  };

  const {
    fieldState: { error },
    field: { value, onChange, ...field },
  } = useController<T, Path<T>>({
    defaultValue,
    disabled,
    name,
    rules: mergedRules,
  });

  return (
    <InputField
      name={name}
      label={t('common.phone')}
      error={error?.message}
      isRequired={!!mergedRules.required}
      className={className}
    >
      <PhoneInput
        {...field}
        id={name}
        placeholder={t('common.phone')}
        defaultCountry="NL"
        international
        withCountryCallingCode
        countryCallingCodeEditable={false}
        disabled={disabled}
        value={value || undefined}
        onChange={(nextValue) => onChange(nextValue ?? '')}
        numberInputProps={{
          className:
            'border border-border-input-enabled py-3 px-4 rounded-input focus:border-border-input-focus focus:ring-border-input-focus/30 focus:ring-3 outline-none placeholder:text-content-disabled',
        }}
        {...props}
      />
    </InputField>
  );
}
